/**
 * Shared catalog of human-friendly metadata for the platform's feature flags.
 *
 * The DB `feature_flags` table is the runtime source of truth for whether a
 * flag is on/off (seeded from `site.config.ts` `features`), but it only stores
 * the raw key as its `name` and an empty `description` for hydrated rows. This
 * catalog supplies the label, hint, and grouping the UI shows for each known
 * key. It is consumed by:
 *   - the site-creation wizard (WizardStep3Features) at site setup, and
 *   - the admin Feature Toggles page (toggling flags on a live tenant).
 *
 * Keep this the single source of truth — adding a flag here surfaces it in
 * both places. Flags present in the DB but absent here fall back to their
 * stored name/description under an "Other" group in the admin UI.
 */

export interface FeatureConfig {
  key: string;
  label: string;
  hint: string;
  group: string;
}

export const FEATURE_GROUPS: Record<string, FeatureConfig[]> = {
  'Site Type': [
    { key: 'ecommerce', label: 'Ecommerce', hint: 'Enable shopping cart, orders, shipping, and product catalog', group: 'Site Type' },
  ],
  'Core Commerce': [
    { key: 'gift_cards', label: 'Gift Cards', hint: 'Let customers purchase and redeem store gift cards', group: 'Core Commerce' },
    { key: 'product_bundles', label: 'Product Bundles', hint: 'Group products into discounted bundles', group: 'Core Commerce' },
    { key: 'volume_discounts', label: 'Volume Discounts', hint: 'Offer tiered pricing for bulk purchases', group: 'Core Commerce' },
    { key: 'customizable_products', label: 'Customizable Products', hint: 'Let customers personalize products before purchase', group: 'Core Commerce' },
  ],
  'Marketing': [
    { key: 'loyalty_program', label: 'Loyalty Program', hint: 'Reward repeat customers with points and perks', group: 'Marketing' },
    { key: 'referral_program', label: 'Referral Program', hint: 'Encourage customers to refer friends for rewards', group: 'Marketing' },
    { key: 'newsletter', label: 'Newsletter', hint: 'Collect emails and send marketing campaigns', group: 'Marketing' },
    { key: 'social_proof', label: 'Social Proof', hint: 'Show recent purchases and activity notifications', group: 'Marketing' },
    { key: 'countdown_timers', label: 'Countdown Timers', hint: 'Create urgency with sale and offer timers', group: 'Marketing' },
    { key: 'abandoned_cart_recovery', label: 'Abandoned Cart Recovery', hint: 'Automatically email customers who left items in cart', group: 'Marketing' },
  ],
  'Search & Discovery': [
    { key: 'meilisearch', label: 'Full-text Search', hint: 'Lightning-fast search with typo tolerance', group: 'Search & Discovery' },
    { key: 'product_attributes', label: 'Product Attributes', hint: 'Filterable attributes like size, color, material', group: 'Search & Discovery' },
    { key: 'back_in_stock', label: 'Back-in-Stock Alerts', hint: 'Notify customers when sold-out items return', group: 'Search & Discovery' },
    { key: 'wishlist', label: 'Wishlist', hint: 'Let customers save products for later', group: 'Search & Discovery' },
  ],
  'Content': [
    { key: 'reviews', label: 'Product Reviews', hint: 'Customer ratings and written reviews on products', group: 'Content' },
    { key: 'ai_features', label: 'AI Features', hint: 'AI-powered descriptions, recommendations, and chat', group: 'Content' },
    { key: 'local_events', label: 'Local Events', hint: 'Show AI-powered community events on the Local Guide page', group: 'Content' },
  ],
  'Advanced': [
    { key: 'dropshipping', label: 'Dropshipping', hint: 'Fulfill orders through third-party suppliers', group: 'Advanced' },
    { key: 'marketplace_channels', label: 'Marketplace Channels', hint: 'Sync products to Amazon, eBay, and more', group: 'Advanced' },
    { key: 'department_templates', label: 'Department Templates', hint: 'Pre-built layouts for different product departments', group: 'Advanced' },
    { key: 'payment_splitting', label: 'Payment Splitting', hint: 'Split payments between multiple vendors', group: 'Advanced' },
    { key: 'job_queue_dashboard', label: 'Job Queue Dashboard', hint: 'Monitor background jobs and async tasks', group: 'Advanced' },
    { key: 'ownersbox', label: 'Ownersbox Monitoring', hint: 'Link this site’s admin to the Ownersbox uptime and alerting dashboard. Enable only for owners who have an Ownersbox account — its sign-in is invite-only.', group: 'Advanced' },
  ],
  'Online Courses': [
    { key: 'courses', label: 'Courses', hint: 'Create and sell online courses with lessons', group: 'Online Courses' },
    { key: 'course_subscriptions', label: 'Course Subscriptions', hint: 'Recurring access plans for course content', group: 'Online Courses' },
    { key: 'course_bundles', label: 'Course Bundles', hint: 'Package multiple courses at a discount', group: 'Online Courses' },
    { key: 'course_certificates', label: 'Course Certificates', hint: 'Issue completion certificates to students', group: 'Online Courses' },
    { key: 'course_drip_content', label: 'Drip Content', hint: 'Release course lessons on a schedule', group: 'Online Courses' },
    { key: 'course_quizzes', label: 'Course Quizzes', hint: 'Test knowledge with quizzes and assessments', group: 'Online Courses' },
    { key: 'founders_pricing', label: 'Founders Pricing', hint: 'Early-bird pricing tiers for initial customers', group: 'Online Courses' },
    { key: 'live_sessions', label: 'Live Sessions', hint: 'Schedule and host live video sessions', group: 'Online Courses' },
  ],
  'Industry-Specific': [
    { key: 'seed_specific_fields', label: 'Seed/Plant Fields', hint: 'Specialized fields for seed and plant products', group: 'Industry-Specific' },
  ],
  'Lodging': [
    { key: 'welcome_screens', label: 'Welcome Screens', hint: 'Themed in-unit welcome posters on a WiFi picture frame, personalized per arriving guest', group: 'Lodging' },
  ],
};

export const ALL_FEATURES: FeatureConfig[] = Object.values(FEATURE_GROUPS).flat();

/** Ordered list of group names, matching the declaration order above. */
export const FEATURE_GROUP_ORDER: string[] = Object.keys(FEATURE_GROUPS);

const FEATURE_META_BY_KEY: Map<string, FeatureConfig> = new Map(
  ALL_FEATURES.map((f) => [f.key, f]),
);

/** Look up the catalog metadata for a flag key, or undefined if unknown. */
export function getFeatureMeta(key: string): FeatureConfig | undefined {
  return FEATURE_META_BY_KEY.get(key);
}
